// Industry DLP pack catalogue, activation, per-rule control and import/export
// (mirrors services/firewall/src/services/dlpPacks.ts)

export interface DlpPackRule {
  id: string;
  name: string;
  regex: string;
  severity: 'low' | 'med' | 'high';
  validator: string | null;
  sample: string | null;
  enabled: boolean;
}

export interface DlpPackInfo {
  id: string;
  name: string;
  industry: string;
  description: string;
  custom: boolean;
  active: boolean;
  rules: DlpPackRule[];
}

export interface PackSettings {
  active_packs: string[];
  disabled_rules: string[];
}

async function failure(res: Response): Promise<Error> {
  let msg = `${res.status} ${res.statusText}`;
  try {
    const body = await res.json();
    msg = body.message || body.error || msg;
  } catch {
    /* keep status text */
  }
  return new Error(msg);
}

export async function getDlpPacks(apiUrl: string): Promise<DlpPackInfo[]> {
  const res = await fetch(`${apiUrl}/admin/gateway/dlp/packs`);
  if (!res.ok) throw await failure(res);
  return (await res.json()).packs as DlpPackInfo[];
}

/** Save the active packs and/or the per-rule opt-outs. Throws (with the server's message) if the save fails. */
export async function savePackSettings(apiUrl: string, change: Partial<PackSettings>): Promise<PackSettings> {
  const res = await fetch(`${apiUrl}/admin/gateway/dlp`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(change),
  });
  if (!res.ok) throw await failure(res);
  const { dlp } = await res.json();
  return { active_packs: dlp.active_packs, disabled_rules: dlp.disabled_rules };
}

/** Download a pack as an editable JSON file. */
export async function downloadPack(apiUrl: string, packId: string): Promise<void> {
  const res = await fetch(`${apiUrl}/admin/gateway/dlp/packs/${encodeURIComponent(packId)}/export`);
  if (!res.ok) throw await failure(res);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `dlp-pack-${packId}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface ImportResult {
  pack: { id: string; name: string; rules: number };
  replaced: boolean;
  warnings: string[];
}

/** Load a (customer-edited) pack file. Creates the pack, or replaces a custom pack with the same id. */
export async function importPack(apiUrl: string, fileText: string): Promise<ImportResult> {
  let doc: unknown;
  try {
    doc = JSON.parse(fileText);
  } catch (e) {
    throw new Error(`Not valid JSON: ${(e as Error).message}`);
  }
  const res = await fetch(`${apiUrl}/admin/gateway/dlp/packs/import`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(doc),
  });
  if (!res.ok) throw await failure(res);
  return res.json();
}

export async function deletePack(apiUrl: string, packId: string): Promise<void> {
  const res = await fetch(`${apiUrl}/admin/gateway/dlp/packs/${encodeURIComponent(packId)}`, { method: 'DELETE' });
  if (!res.ok) throw await failure(res);
}

export interface DlpTestResult {
  redacted: string;
  violations: string[];
}

/** Dry-run the live DLP configuration (active packs + built-ins + custom rules) on pasted text. */
export async function testDlp(apiUrl: string, text: string): Promise<DlpTestResult> {
  const res = await fetch(`${apiUrl}/admin/gateway/dlp/test`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw await failure(res);
  return res.json();
}
